import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { TrendingUp, TrendingDown, Clock, Activity } from "lucide-react";
import api from "../lib/api";
import { logError } from "../lib/logger";

function fmtPrice(symbol, v) {
  if (v === null || v === undefined) return "—";
  const s = (symbol || "").toUpperCase();
  let dec = 2;
  if (s.includes("JPY")) dec = 3;
  else if (s.includes("EUR") && s.includes("USD")) dec = 5;
  return v.toLocaleString("it-IT", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function fmtUsd(v) {
  const sign = v >= 0 ? "+" : "-";
  return `${sign}${Math.abs(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}$`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return "—"; }
}

function SummaryStat({ label, value, color }) {
  return (
    <div className="bg-black/30 border border-white/10 rounded-md p-3">
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-0.5 ${color || "text-white"}`}>{value}</div>
    </div>
  );
}

function ActiveRow({ t }) {
  const isBuy = t.direction === "BUY";
  return (
    <div data-testid={`active-row-${t.seq}`} className="flex items-center gap-3 py-3 px-3 border-b border-white/5 hover:bg-white/5 transition-colors">
      <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${t.pending ? "bg-zinc-500/10 text-zinc-300" : "bg-blue-500/10 text-blue-400"}`}>
        {t.pending ? <Clock size={15} /> : <Activity size={15} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-zinc-500">ID {t.seq}</span>
          <span className="font-semibold text-sm text-white">{t.symbol}</span>
          <span className={`text-xs font-medium ${isBuy ? "text-emerald-400" : "text-red-400"}`}>{t.direction}</span>
          <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${t.pending ? "text-zinc-300 border border-zinc-500/40" : "text-blue-400 border border-blue-500/40"}`}>
            {t.pending ? "PENDENTE" : "ATTIVO"}
          </span>
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span>Entry {fmtPrice(t.symbol, t.entry)}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-emerald-500/80">TP {fmtPrice(t.symbol, t.tp)}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-red-500/80">SL {fmtPrice(t.symbol, t.sl)}</span>
          {t.strategy_label && (<><span className="text-zinc-700">·</span><span>{t.strategy_label === "LIQUIDITY POOL" ? "Liquidity Pool" : "Stoch Trend"}{t.timeframe ? ` ${t.timeframe}` : ""}</span></>)}
        </div>
      </div>
    </div>
  );
}

function TpRow({ t }) {
  return (
    <div data-testid={`tp-row-${t.seq}`} className="flex items-center gap-3 py-3 px-3 border-b border-white/5 hover:bg-white/5 transition-colors">
      <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-emerald-500/10 text-emerald-400">
        <TrendingUp size={15} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-zinc-500">ID {t.seq}</span>
          <span className="font-semibold text-sm text-white">{t.symbol}</span>
          <span className={`text-xs font-medium ${t.direction === "BUY" ? "text-emerald-400" : "text-red-400"}`}>{t.direction}</span>
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span>Entry {fmtPrice(t.symbol, t.entry)}</span>
          <span className="text-zinc-700">·</span>
          <span className="text-emerald-500/80">TP {fmtPrice(t.symbol, t.tp)}</span>
          {t.strategy_label && (<><span className="text-zinc-700">·</span><span>{t.strategy_label === "LIQUIDITY POOL" ? "Liquidity Pool" : "Stoch Trend"}{t.timeframe ? ` ${t.timeframe}` : ""}</span></>)}
          <span className="text-zinc-700">·</span>
          <span>{fmtDate(t.closed_at)}</span>
        </div>
      </div>
      {t.pnl !== undefined && t.pnl !== null && (
        <div className="shrink-0 text-right">
          <div className="font-mono text-sm font-semibold text-emerald-400">{fmtUsd(t.pnl)}</div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-500/70">PROFIT</div>
        </div>
      )}
    </div>
  );
}

const CONF = {
  active: { status: "active", title: "Trade Attivi e Pendenti", icon: Activity, iconColor: "text-blue-400",
    desc: "Elenco dei segnali attualmente aperti (attivi) e degli ordini LIMIT non ancora eseguiti (pendenti)." },
  tp: { status: "tp_hit", title: "TP Raggiunti", icon: TrendingUp, iconColor: "text-emerald-400",
    desc: "Elenco dei segnali che hanno raggiunto il Take Profit, con il relativo profitto a size fissa 0,10 lotti." },
};

export function SignalsListModal({ open, onClose, variant = "active" }) {
  const conf = CONF[variant] || CONF.active;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/signals/list-by-status", { params: { status: conf.status } })
      .then((r) => setData(r.data))
      .catch((e) => logError("Errore elenco segnali:", e))
      .finally(() => setLoading(false));
  }, [open, conf.status]);

  const s = data?.summary;
  const Icon = conf.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`signals-list-modal-${variant}`} className="max-w-2xl bg-[#0d0d0d] border-white/10 text-white max-h-[85vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Icon size={18} className={conf.iconColor} /> {conf.title}
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1 text-left">{conf.desc}</p>
        </DialogHeader>

        {s && (
          <div className="px-6 py-4 border-b border-white/10">
            <div className="grid grid-cols-3 gap-3">
              {variant === "active" ? (
                <>
                  <SummaryStat label="Totale" value={s.count} />
                  <SummaryStat label="Attivi" value={s.active} color="text-blue-400" />
                  <SummaryStat label="Pendenti" value={s.pending} color="text-zinc-200" />
                </>
              ) : (
                <>
                  <SummaryStat label="TP raggiunti" value={s.count} color="text-emerald-400" />
                  <SummaryStat label="Profitto totale" value={fmtUsd(s.total_pnl)} color="text-emerald-400" />
                  <SummaryStat label="Size fissa" value="0,10 lotti" />
                </>
              )}
            </div>
          </div>
        )}

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-16 text-zinc-500 text-sm">Caricamento...</div>
          ) : !data || data.items.length === 0 ? (
            <div data-testid="signals-list-empty" className="text-center py-16 text-zinc-500 text-sm">Nessun trade al momento.</div>
          ) : (
            <div>
              {data.items.map((t) => variant === "active"
                ? <ActiveRow key={t.seq} t={t} />
                : <TpRow key={t.seq} t={t} />)}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
